import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import { supabase } from "../lib/supabaseClient";

function prettyStatus(s) {
  const statusMap = {
    AWAITING_PAYMENT: { label: "Awaiting Payment", color: "bg-yellow-100 text-yellow-800" },
    PAID: { label: "Payment Received", color: "bg-green-100 text-green-800" },
    SHIPPED: { label: "Shipped", color: "bg-blue-100 text-blue-800" },
    CANCELLED: { label: "Cancelled", color: "bg-red-100 text-red-800" },
  };
  return statusMap[s] || { label: s, color: "bg-gray-100 text-gray-800" };
}

export default function MyOrders() {
  useEffect(() => {
  document.title = "My Orders | HD Labs";
}, []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    let mounted = true;

    async function init() {
      setLoading(true);
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      if (!session) {
        navigate("/login");
        return;
      }
      const userId = session.user.id;
      const userEmail = session.user.email || "";
      if (mounted) setEmail(userEmail);

      const { data: ordersData, error } = await supabase
        .from("orders")
        .select("id, order_number, created_at, status, total")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (!mounted) return;
      setLoading(false);

      if (error) {
        alert("Failed to load orders: " + error.message);
        return;
      }
      setOrders(ordersData || []);
    }

    init();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate("/login");
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [navigate]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Container padding controls space between last order and footer */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
            <p className="mt-2 text-gray-600">
              Signed in as <span className="font-semibold text-gray-900">{email}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/shop">
              <Button variant="secondary">Shop</Button>
            </Link>
            <Button onClick={signOut}>Sign out</Button>
          </div>
        </div>

        {/* Row-based order list */}
        <div className="mt-8 flex flex-col gap-6">
          {loading ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 animate-pulse">
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
              <p>No orders yet.</p>
              <div className="mt-4">
                <Link to="/shop">
                  <Button>Browse products</Button>
                </Link>
              </div>
            </div>
          ) : (
            orders.map((o) => {
              const status = prettyStatus(o.status);
              return (
                <div
                  key={o.id}
                  className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="mb-4">
                    <div className="text-sm text-gray-500">
                      Order{" "}
                      <span className="font-semibold text-gray-900">
                        {o.order_number || o.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                      {status.label}
                    </span>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-lg font-semibold text-gray-900">
                        ${Number(o.total || 0).toFixed(2)}
                      </div>
                      <div className="mt-3">
                        <Link to={`/order/${o.id}`}>
                          <Button variant="secondary">View</Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}